export const CommunityMembershipTierBillingType = {
  FREE: 'free',
  SUBSCRIPTION: 'subscription',
  LIFETIME: 'lifetime',
} as const;

export type CommunityMembershipTierBillingTypeValue =
  (typeof CommunityMembershipTierBillingType)[keyof typeof CommunityMembershipTierBillingType];

export const CommunityMembershipWaitlistStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PROMOTED: 'promoted',
} as const;

export type CommunityMembershipWaitlistStatusValue =
  (typeof CommunityMembershipWaitlistStatus)[keyof typeof CommunityMembershipWaitlistStatus];

export type TierAccessContext = {
  accessRank: number;
} | null;

export type CommunityMembershipTierRef = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  accessRank: number;
  billingType: CommunityMembershipTierBillingTypeValue;
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
  lifetimePriceCents?: number | null;
  trialDays?: number | null;
  memberCap?: number | null;
  requiresApproval?: boolean;
  waitlistWhenFull?: boolean;
  enabled?: boolean;
  isDefault?: boolean;
};

export type CommunityMembershipCouponRef = {
  code: string;
  percentOff?: number | null;
  amountOffCents?: number | null;
  maxRedemptions?: number | null;
  redemptionCount?: number;
  expiresAt?: Date | string | null;
  tierIds?: string[] | null;
  enabled?: boolean;
};

export function slugifyMembershipTierName(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return base || 'tier';
}

export function memberTierMeetsMinimum(
  memberTier: { accessRank: number } | null | undefined,
  requiredTier: { accessRank: number } | null | undefined,
): boolean {
  if (!requiredTier) return true;
  if (!memberTier) return false;
  return memberTier.accessRank >= requiredTier.accessRank;
}

export function isPaidMembershipTier(
  tier: Pick<CommunityMembershipTierRef, 'billingType'>,
): boolean {
  return (
    tier.billingType === CommunityMembershipTierBillingType.SUBSCRIPTION ||
    tier.billingType === CommunityMembershipTierBillingType.LIFETIME
  );
}

export function tierHasCheckoutPrice(
  tier: Pick<
    CommunityMembershipTierRef,
    'billingType' | 'monthlyPriceCents' | 'annualPriceCents' | 'lifetimePriceCents'
  >,
  interval: 'month' | 'year' | 'lifetime',
): boolean {
  if (tier.billingType === CommunityMembershipTierBillingType.FREE) return false;
  if (interval === 'lifetime') {
    return (
      tier.billingType === CommunityMembershipTierBillingType.LIFETIME &&
      tier.lifetimePriceCents != null
    );
  }
  if (tier.billingType !== CommunityMembershipTierBillingType.SUBSCRIPTION) return false;
  return interval === 'year'
    ? tier.annualPriceCents != null
    : tier.monthlyPriceCents != null;
}

export function normalizeCouponCode(code: string): string {
  return code.trim().toUpperCase();
}

export function validateMembershipCoupon(params: {
  coupon: CommunityMembershipCouponRef;
  tierId: string;
  now?: Date;
}): { ok: true } | { ok: false; message: string } {
  const now = params.now ?? new Date();
  if (params.coupon.enabled === false) {
    return { ok: false, message: 'Coupon is not active' };
  }
  if (params.coupon.expiresAt) {
    const expiresAt =
      params.coupon.expiresAt instanceof Date
        ? params.coupon.expiresAt
        : new Date(params.coupon.expiresAt);
    if (expiresAt.getTime() <= now.getTime()) {
      return { ok: false, message: 'Coupon has expired' };
    }
  }
  if (
    params.coupon.maxRedemptions != null &&
    (params.coupon.redemptionCount ?? 0) >= params.coupon.maxRedemptions
  ) {
    return { ok: false, message: 'Coupon redemption limit reached' };
  }
  if (params.coupon.percentOff == null && params.coupon.amountOffCents == null) {
    return { ok: false, message: 'Coupon is misconfigured' };
  }
  if (params.coupon.percentOff != null && (params.coupon.percentOff < 1 || params.coupon.percentOff > 100)) {
    return { ok: false, message: 'Coupon is misconfigured' };
  }
  const tierIds = params.coupon.tierIds ?? null;
  if (tierIds && tierIds.length > 0 && !tierIds.includes(params.tierId)) {
    return { ok: false, message: 'Coupon does not apply to this tier' };
  }
  return { ok: true };
}

export function isTierAtCapacity(activeMemberCount: number, memberCap?: number | null): boolean {
  if (memberCap == null) return false;
  return activeMemberCount >= memberCap;
}

export function grantsLifetimeMembershipBillingAccess(
  stripeBillingStatus: string | null | undefined,
): boolean {
  return stripeBillingStatus === 'lifetime';
}

export function sortMembershipTiers<T extends { sortOrder: number; name: string }>(tiers: T[]): T[] {
  return [...tiers].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}
